import React, { memo } from 'react';
import { graphql, useFragment } from 'react-relay';
import { Box, xcss } from '@atlaskit/primitives';
import { StatusLozenge as IssueStatusFieldReadonly } from '@atlassian/jira-issue-field-status/src/ui/status-lozenge/index.tsx';
import {
	useHoverPopoverContentRef,
	type HoverPopoverContentRef,
} from '@atlassian/jira-hover-popover/src/controllers/hover-popover-content-provider/index.tsx';
import { fg } from '@atlassian/jira-feature-gating';
import ErrorCell from '../../error-cell/index.tsx';
import { useIsSingleLineRowHeightEnabled } from '../../../../controllers/features/selectors.tsx';
import type { IssueStatusCategoryCell$key } from './__generated__/IssueStatusCategoryCell.graphql';

export type Props = {
	fieldRef: IssueStatusCategoryCell$key;
};

export const IssueStatusCategoryCell = memo<Props>(({ fieldRef }: Props) => {
	const data = useFragment<IssueStatusCategoryCell$key>(
		graphql`
			fragment IssueStatusCategoryCell on JiraStatusCategoryField {
				id
				statusCategory {
					name
					statusCategoryId
				}
			}
		`,
		fieldRef,
	);
	const { statusCategory } = data;
	const { name } = statusCategory;
	const statusCategoryId = parseInt(statusCategory.statusCategoryId, 10);

	const isSingleLineRowHeightEnabled = useIsSingleLineRowHeightEnabled();

	const hoverPopoverRefFactory = isSingleLineRowHeightEnabled
		? // eslint-disable-next-line react-hooks/react-compiler
			// eslint-disable-next-line react-hooks/rules-of-hooks
			useHoverPopoverContentRef(data.id)
		: null;

	let hoverPopoverRef: HoverPopoverContentRef = null;
	if (hoverPopoverRefFactory) {
		hoverPopoverRef = hoverPopoverRefFactory({
			content: statusCategory.name,
		});
	}

	if (Number.isNaN(statusCategoryId) || statusCategoryId === -1 || !name?.length) {
		return <ErrorCell cellType="statusCategory" reason="Missing required fields" />;
	}

	if (!isSingleLineRowHeightEnabled)
		return (
			<Box xcss={fg('nin_issue_table_rtl_fix') ? alignmentFixStyles : alignmentFixStylesOld}>
				{/* IssueStatusFieldReadonly handles rendering the statusCategory in a manner consistent to status - rendering the correct color for a given status category.
                Status id is intentially left blank
             */}
				<IssueStatusFieldReadonly
					value={{ id: '', name, statusCategory: { id: statusCategoryId } }}
				/>
			</Box>
		);

	return (
		<Box xcss={fg('nin_issue_table_rtl_fix') ? alignmentFixStyles : alignmentFixStylesOld}>
			{/* IssueStatusFieldReadonly handles rendering the statusCategory in a manner consistent to status - rendering the correct color for a given status category.
                Status id is intentially left blank */}
			<IssueStatusFieldReadonly
				value={{ id: '', name, statusCategory: { id: statusCategoryId } }}
				contentRef={hoverPopoverRef}
			/>
		</Box>
	);
});

// Status lozenges have a 2px invisible border around them to keep them in the same place if they enter an "error" state (where a border colour is added).
// Refer to: src/packages/issue/fields/status/src/common/ui/status-lozenge/main.tsx

const alignmentFixStylesOld = xcss({
	// eslint-disable-next-line @atlaskit/design-system/no-physical-properties
	marginLeft: 'space.negative.025',
});

const alignmentFixStyles = xcss({
	marginInlineStart: 'space.negative.025',
});
